/**
 * Avatar disc shared by the float and cluster fragments.
 *
 * Two layers, always both: an initials disc, and — when the event has an
 * `imageKey` — the photo on top, clipped to a circle. Fragments are pure and
 * may not attach listeners, so there is no `onerror` to fall back on; drawing
 * the initials underneath means a missing or still-loading photo degrades to
 * initials instead of a hole.
 */
import type { CalEvent } from '@circle/shared';
import { n, svgEl } from '../../core/svg';

/** Worker route for a stored image. */
export function imageUrl(key: string): string {
  return `/img/${encodeURIComponent(key)}`;
}

/** "Anna Lindqvist" → "AL"; "Midsummer" → "M". Grapheme-aware enough for names. */
export function initials(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = [...words[0]!][0] ?? '';
  const last = words.length > 1 ? ([...words[words.length - 1]!][0] ?? '') : '';
  return (first + last).toLocaleUpperCase();
}

export interface AvatarOptions {
  cx: number;
  cy: number;
  r: number;
  event: CalEvent;
  colour: string;
  /** Ring stroke width in screen px; 0 for none. */
  ring?: number;
  /** Overrides the initials font size (default: 0.72 * r). */
  fontSize?: number;
}

export function avatarDisc(opts: AvatarOptions): SVGGElement {
  const { cx, cy, r, event, colour } = opts;
  const ring = opts.ring ?? 1.25;
  const g = svgEl('g', { 'pointer-events': 'none' });

  g.append(
    svgEl('circle', {
      cx: n(cx),
      cy: n(cy),
      r: n(r),
      fill: 'var(--avatar-bg)',
    }),
  );

  const text = svgEl('text', {
    x: n(cx),
    y: n(cy + (opts.fontSize ?? r * 0.72) * 0.35),
    'text-anchor': 'middle',
    'font-size': n(opts.fontSize ?? r * 0.72),
    'font-weight': 600,
    'letter-spacing': 0.5,
    fill: colour,
    opacity: 0.85,
  });
  text.textContent = initials(event.title);
  g.append(text);

  if (event.imageKey) {
    // CSS basic-shape clip: no <clipPath> element, so no document-unique id to
    // invent — fragments stay pure and duplicable.
    g.append(
      svgEl('image', {
        href: imageUrl(event.imageKey),
        x: n(cx - r),
        y: n(cy - r),
        width: n(2 * r),
        height: n(2 * r),
        preserveAspectRatio: 'xMidYMid slice',
        'clip-path': `circle(${n(r)}px at ${n(r)}px ${n(r)}px)`,
      }),
    );
  }

  if (ring > 0) {
    g.append(
      svgEl('circle', {
        cx: n(cx),
        cy: n(cy),
        r: n(r),
        fill: 'none',
        stroke: colour,
        'stroke-width': ring,
        opacity: 0.55,
      }),
    );
  }

  return g;
}

/** Clip a name to `max` characters, ending in an ellipsis when cut. */
export function ellipsize(text: string, max: number): string {
  const chars = [...text];
  return chars.length <= max ? text : `${chars.slice(0, Math.max(1, max - 1)).join('').trimEnd()}…`;
}
